import React, { useState, useEffect } from 'react';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { SelectButton } from 'primereact/selectbutton';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Chart } from 'primereact/chart';
import { BlockUI } from 'primereact/blockui';
import { ProgressSpinner } from 'primereact/progressspinner';
import * as XLSX from 'xlsx';
import { controlTramos } from '../../services/Logistica/DoleService';

/**
 * Control de tramos de DOLE (correo del 31/10/2026): R9/R11 -> patio seco y R11 -> R9 solo chasis.
 * Conteos y gráficos propios; los de VACIO/LLENO de Logística no cambian. Desde el 03/11/2026 usa los filtros de arriba
 * (año, mes, semana, día) y consulta cuando se aprieta la lupa de Logística; muestra también los viajes sin seguimiento
 * (control apagado o viaje anterior a encenderlo).
 */

const CIRCUITOS = [
    { label: 'Todos', value: 'TODOS' },
    { label: 'Patio seco', value: 'PATIO_SECO' },
    { label: 'Solo chasis', value: 'CHASIS' },
];
const NOMBRE_CIRCUITO = { PATIO_SECO: 'Patio seco (R9/R11 → P/S)', CHASIS: 'Solo chasis (R11 → R9)' };
const COLOR_ESTADO = {
    'EN R9/R11': '#64748b',
    'RUTA PATIO SECO': '#2563eb',
    'EN PATIO SECO': '#15803d',
    'RETIRANDO CHASIS': '#b45309',
    'CHASIS EN R9': '#15803d',
    'SIN CIERRE': '#6b7280',
    'SIN SEGUIMIENTO': '#94a3b8',
};
const COLOR_PS = '#2563eb';
const COLOR_CHASIS = '#b45309';
const COLOR_DELINCUENTE = '#b91c1c';

/** Filtros de Logística a parámetros del control de tramos: '' / null / -1 = todos. */
export const parametrosFiltro = (filtro) => {
    const entero = (v) => (v === null || v === undefined || v === '' || Number.isNaN(Number(v)) ? -1 : Number(v));
    return { anio: entero(filtro.anio), mes: entero(filtro.mes), semana: entero(filtro.semana), dia: entero(filtro.dia) };
};

export const aIso = (d) => {
    const p = (n) => ('0' + n).slice(-2);
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

export const minutosATexto = (m) => {
    if (m == null) return '';
    const h = Math.floor(m / 60);
    return h > 0 ? `${h} h ${m % 60} min` : `${m} min`;
};

const ControlTramos = ({ filtro }) => {
    const [circuito, setCircuito] = useState('TODOS');
    const [datos, setDatos] = useState({ filas: [], resumen: null });
    const [cargando, setCargando] = useState(false);
    const [error, setError] = useState('');

    // consulta con la lupa de arriba (filtro cambia en cada búsqueda) y al cambiar de circuito
    useEffect(() => {
        if (!filtro) return undefined;
        const p = parametrosFiltro(filtro);
        if (p.anio < 0 || p.mes < 0) {
            setDatos({ filas: [], resumen: null });
            setError('Elija año y mes arriba');
            return undefined;
        }
        let vigente = true; // si llega otra búsqueda antes, esta respuesta se descarta
        setCargando(true);
        setError('');
        controlTramos(p.anio, p.mes, p.semana, p.dia, circuito)
            .then((d) => { if (vigente) setDatos(d); })
            .catch((e) => {
                if (!vigente) return;
                setDatos({ filas: [], resumen: null });
                setError(e.message || 'No se pudo consultar el control de tramos');
            })
            .finally(() => { if (vigente) setCargando(false); });
        return () => { vigente = false; };
    }, [filtro, circuito]);

    const resumen = datos.resumen || { porCircuito: {}, porDia: [], porPatio: [] };
    const circuitosVisibles = circuito === 'TODOS' ? ['PATIO_SECO', 'CHASIS'] : [circuito];

    const datosDia = {
        labels: (resumen.porDia || []).map((d) => d.dia.substring(5)),
        datasets: [
            ...(circuitosVisibles.includes('PATIO_SECO') ? [{ type: 'bar', label: 'Patio seco', backgroundColor: COLOR_PS, stack: 'v', data: (resumen.porDia || []).map((d) => d.PATIO_SECO) }] : []),
            ...(circuitosVisibles.includes('CHASIS') ? [{ type: 'bar', label: 'Solo chasis', backgroundColor: COLOR_CHASIS, stack: 'v', data: (resumen.porDia || []).map((d) => d.CHASIS) }] : []),
            { type: 'bar', label: 'Delincuentes', backgroundColor: COLOR_DELINCUENTE, stack: 'd', data: (resumen.porDia || []).map((d) => d.delincuentes) },
        ],
    };
    const datosPatio = {
        labels: (resumen.porPatio || []).map((p) => p.patio),
        datasets: [
            { label: 'Viajes', backgroundColor: COLOR_PS, data: (resumen.porPatio || []).map((p) => p.total) },
            { label: 'Delincuentes', backgroundColor: COLOR_DELINCUENTE, data: (resumen.porPatio || []).map((p) => p.delincuentes) },
        ],
    };
    const opcionesBarras = {
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'bottom' },
            datalabels: { color: '#fff', font: { weight: 'bold' }, display: (ctx) => Number(ctx.dataset.data[ctx.dataIndex]) > 0 },
        },
        scales: { x: { stacked: true }, y: { stacked: true, beginAtZero: true, ticks: { precision: 0 } } },
    };
    const opcionesPatio = {
        maintainAspectRatio: false,
        indexAxis: 'y',
        plugins: {
            legend: { position: 'bottom' },
            datalabels: { color: '#fff', font: { weight: 'bold' }, display: (ctx) => Number(ctx.dataset.data[ctx.dataIndex]) > 0 },
        },
        scales: { x: { beginAtZero: true, ticks: { precision: 0 } } },
    };

    const filasVisibles = (datos.filas || []).filter((f) => circuito === 'TODOS' || f.circuito === circuito);

    const exportar = () => {
        const hoja = XLSX.utils.json_to_sheet(filasVisibles.map((f) => ({
            Circuito: NOMBRE_CIRCUITO[f.circuito] || f.circuito,
            Booking: f.booking, Contenedor: f.contenedor, Candado: f.candado,
            Origen: f.origen, Destino: f.destino, Estado: f.estado, 'Estado del viaje': f.estadoViaje,
            Registro: f.fechaInicio, F1: f.f1, F2: f.f2, 'Duración (min)': f.minutos,
            'Límite (h)': f.horasLimite, Delincuente: f.delincuente ? f.delincuenteFecha : '',
            Alerta: f.alerta ? `${f.alertaFecha} ${f.alertaDetalle || ''}` : '',
            'Vacío siguiente': f.siguienteContenedor || f.viajeSiguienteId || '',
        })));
        const libro = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(libro, hoja, 'Control de tramos');
        const p = parametrosFiltro(filtro || {});
        const parte = (n, v) => (v < 0 ? '' : `_${n}${v}`);
        XLSX.writeFile(libro, `control_tramos_${p.anio}_${('0' + p.mes).slice(-2)}${parte('sem', p.semana)}${parte('dia', p.dia)}.xlsx`);
    };

    const tarjeta = (c) => {
        const r = (resumen.porCircuito || {})[c] || {};
        const dato = (titulo, valor, color) => (
            <div style={{ minWidth: '6.5rem' }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-color-secondary)', letterSpacing: '0.02em' }}>{titulo}</div>
                <div style={{ fontSize: '1.4rem', fontWeight: 700, color: color || 'inherit', fontVariantNumeric: 'tabular-nums' }}>{valor ?? 0}</div>
            </div>
        );
        return (
            <div key={c} className="card" style={{ flex: '1 1 22rem', marginBottom: 0 }}>
                <h6 style={{ marginTop: 0, color: c === 'CHASIS' ? COLOR_CHASIS : COLOR_PS }}>{NOMBRE_CIRCUITO[c]}</h6>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                    {dato('Total', r.total)}
                    {dato(c === 'CHASIS' ? 'Retirando chasis' : 'En ruta', r.enCurso)}
                    {dato(c === 'CHASIS' ? 'En R9' : 'En patio seco', r.cerrados, '#15803d')}
                    {dato('Delincuentes', r.delincuentes, COLOR_DELINCUENTE)}
                    {dato('Alertas', r.alertas, '#b45309')}
                    {r.sinSeguimiento > 0 && dato('Sin seguimiento', r.sinSeguimiento, '#64748b')}
                    {dato('Tiempo promedio', minutosATexto(r.minutosPromedio) || '—')}
                </div>
            </div>
        );
    };

    const cuerpoEstado = (f) => (
        <span style={{ background: COLOR_ESTADO[f.estado] || '#6b7280', color: '#fff', borderRadius: 4, padding: '2px 6px', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
            {f.estado}
        </span>
    );
    const cuerpoMarca = (si, fecha, titulo) => (si ? <span title={titulo || fecha} style={{ color: COLOR_DELINCUENTE, fontWeight: 700, whiteSpace: 'nowrap' }}>{fecha}</span> : '');

    return (
        <BlockUI blocked={cargando} template={<ProgressSpinner style={{ width: '50px', height: '50px' }} />}>
            <div className="flex flex-wrap align-items-center gap-2 mb-3">
                <SelectButton id="controlTramosCircuito" value={circuito} options={CIRCUITOS} onChange={(e) => e.value && setCircuito(e.value)} />
                <Button icon="pi pi-file-excel" label="Excel" className="p-button-success p-button-outlined" onClick={exportar} disabled={!filasVisibles.length} />
            </div>
            {!filtro && <div className="mb-2" style={{ color: 'var(--text-color-secondary)' }}>Use los filtros de arriba (año, mes, semana, día) y la lupa para consultar.</div>}
            {error && <div className="p-error mb-2">{error}</div>}
            {datos.resumen && (datos.filas || []).some((f) => f.estado === 'SIN SEGUIMIENTO') && (
                <div className="mb-2" style={{ color: 'var(--text-color-secondary)', fontSize: '0.85rem' }}>
                    «Sin seguimiento»: viaje registrado cuando el control de tramos estaba apagado; no tiene F1, F2, delincuente ni alerta.
                </div>
            )}

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem' }}>
                {circuitosVisibles.map(tarjeta)}
            </div>

            <div className="graficosFlex" style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                <div className="card" style={{ flex: '2 1 26rem', marginBottom: 0 }}>
                    <h5>Viajes por día (registro en la garita)</h5>
                    <div style={{ height: '16rem' }}>
                        <Chart type="bar" data={datosDia} plugins={[ChartDataLabels]} options={opcionesBarras} />
                    </div>
                </div>
                {circuitosVisibles.includes('PATIO_SECO') && (
                    <div className="card" style={{ flex: '1 1 20rem', marginBottom: 0 }}>
                        <h5>Por patio seco</h5>
                        <div style={{ height: '16rem' }}>
                            <Chart type="bar" data={datosPatio} plugins={[ChartDataLabels]} options={opcionesPatio} />
                        </div>
                    </div>
                )}
            </div>

            <DataTable value={filasVisibles} size="small" showGridlines responsiveLayout="scroll" paginator rows={10} rowsPerPageOptions={[10, 25, 50]}
                emptyMessage={filtro ? 'Sin viajes de patio seco ni de solo chasis con estos filtros' : 'Consulte con la lupa de arriba'} className="mt-3" sortField="fechaInicio" sortOrder={-1}>
                <Column field="circuito" header="Circuito" body={(f) => (f.circuito === 'CHASIS' ? 'Solo chasis' : 'Patio seco')} sortable />
                <Column field="booking" header="Booking" sortable />
                <Column field="contenedor" header="Contenedor" />
                <Column field="candado" header="Candado" />
                <Column header="Tramo" body={(f) => `${f.origen || '?'} → ${f.destino || '?'}`} />
                <Column field="estado" header="Estado" body={cuerpoEstado} sortable />
                <Column field="estadoViaje" header="Viaje" sortable />
                <Column field="fechaInicio" header="Registro" sortable />
                <Column field="f1" header="F1" body={(f) => f.f1 || ''} />
                <Column field="f2" header="F2" body={(f) => f.f2 || ''} />
                <Column field="minutos" header="Duración" body={(f) => minutosATexto(f.minutos)} sortable />
                <Column field="horasLimite" header="Límite" body={(f) => (f.horasLimite == null ? '' : `${f.horasLimite} h`)} />
                <Column field="delincuente" header="Delincuente" body={(f) => cuerpoMarca(f.delincuente, f.delincuenteFecha)} sortable />
                <Column field="alerta" header="Alerta" body={(f) => cuerpoMarca(f.alerta, f.alertaFecha, f.alertaDetalle)} sortable />
                <Column header="Vacío siguiente" body={(f) => f.circuito === 'CHASIS' ? (f.siguienteContenedor || (f.viajeSiguienteId ? `viaje ${f.viajeSiguienteId}` : 'pendiente')) : ''} />
            </DataTable>
        </BlockUI>
    );
};

export default ControlTramos;
